/**
 * The Admin guide's seven sections, in order: each with its card summary
 * (live values), its body, and the engine settings it ends with.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { FC } from 'react';
import { CalendarClock, Database, LifeBuoy, PencilLine, RadioTower, Send, Sigma, type LucideIcon } from 'lucide-react';
import type { DropGenEngineSettingKey, DropGenEngineSettings } from '@trafficpulse/shared';

import { DaySection } from './sections/flow/DaySection';
import { EditingSection } from './sections/flow/EditingSection';
import { SendingSection } from './sections/flow/SendingSection';
import { StatsSection } from './sections/flow/StatsSection';
import { CachingSection } from './sections/safety/CachingSection';
import { RouteTestSection } from './sections/safety/RouteTestSection';
import { TroubleSection } from './sections/safety/TroubleSection';

export interface GuideSection {
  n: number;
  Icon: LucideIcon;
  title: string;
  summary: (s: DropGenEngineSettings) => string;
  Body: FC<{ s: DropGenEngineSettings }>;
  keys: readonly DropGenEngineSettingKey[];
}

export const GUIDE_SECTIONS: GuideSection[] = [
  { n: 1, Icon: CalendarClock, title: 'The day in one picture', summary: (s) => `Lay out once, send every minute, judge every ${s.verdictEveryMin} min, alert`, Body: DaySection, keys: ['verdictEveryMin'] },
  { n: 2, Icon: Send, title: 'Sending', summary: (s) => `At their time, up to ${s.maxSendsPerMinute} a minute · missed after ${s.lateToleranceMin} min`, Body: SendingSection, keys: ['maxSendsPerMinute', 'lateToleranceMin', 'stuckSendingMin'] },
  { n: 3, Icon: Sigma, title: 'Reading the stats', summary: (s) => `SMSWiser per drop, ${s.statsReadsPerPass} reads a pass · one tracker report per campaign`, Body: StatsSection, keys: ['statsReadsPerPass', 'statsPaceMs', 'statsRereadMin', 'statsFreshHours'] },
  { n: 4, Icon: LifeBuoy, title: 'When something goes wrong', summary: () => 'It continues by itself — a person only when it must', Body: TroubleSection, keys: ['aiAttempts', 'aiConcurrency'] },
  { n: 5, Icon: PencilLine, title: 'Editing during the day', summary: () => 'Saves reach today’s unsent drafts; hand edits are never touched', Body: EditingSection, keys: ['rewriteLeadMin'] },
  { n: 6, Icon: RadioTower, title: 'Route tests', summary: () => 'Test drops before the window — the best-scored gateway takes the day', Body: RouteTestSection, keys: [] },
  { n: 7, Icon: Database, title: 'Caching', summary: (s) => `Text rankings ${s.textRankingCopyHours} h · list tags ${s.listTagsCacheHours} h · previews never cached`, Body: CachingSection, keys: ['textRankingCopyHours', 'textRankingWarmHours', 'listTagsCacheHours', 'groupTagsLastDays', 'groupTagsBudgetMs'] },
];
